import React, { useState, useEffect, useCallback } from 'react';
import { Card, Row, Col, Spinner, Alert, Form, Button, Table, Badge } from 'react-bootstrap';
import budgetService from '../../../services/budgetService';

const FORD_BLUE = '#003478';

const fmt = (n, dec = 0) => {
  if (n == null || isNaN(n)) return '—';
  return Number(n).toLocaleString('en-US', { minimumFractionDigits: dec, maximumFractionDigits: dec });
};
const fmtCurrency = (n) => (n == null || isNaN(n) ? '—' : '$' + fmt(n, 2));

const fmtCrmCell = (value, crmAplica) => {
  if (crmAplica === false) return <span className="text-muted" title="Categoría digital sin familia CRM">N/A</span>;
  if (value == null || isNaN(value)) return '—';
  return fmt(value);
};

const REPORT_TYPES = [
  { id: 'all', label: 'Todos (ZIP)', icon: 'file-archive', desc: 'FUNNEL + Análisis + Creativos + Planificación' },
  { id: 'funnel', label: 'Reporte Funnel', icon: 'filter', desc: 'Archivo principal — data cruda DW por modelo, campaña y anuncio' },
  { id: 'analisis', label: 'Análisis Funnel', icon: 'chart-line', desc: 'Funnel por modelo y canal con resumen ejecutivo' },
  { id: 'creativos', label: 'Resultados Creativos', icon: 'images', desc: 'Detalle de avisos, formato, gancho y plataforma' },
  { id: 'planificacion', label: 'Planificación Inversión', icon: 'calculator', desc: 'Forecast y tracking de métricas digitales' },
];

const yesterdayIso = () => {
  const d = new Date();
  d.setDate(d.getDate() - 1);
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${mm}-${dd}`;
};

const firstOfMonthIso = () => {
  const d = new Date();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  return `${d.getFullYear()}-${mm}-01`;
};

const FordReportesPage = () => {
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(null);
  const [error, setError] = useState(null);
  const [dailyDate, setDailyDate] = useState(yesterdayIso());
  const [dailyGenerating, setDailyGenerating] = useState(false);
  const [dailyNotice, setDailyNotice] = useState(null);
  const [periods, setPeriods] = useState([]);
  const [periodoDesde, setPeriodoDesde] = useState('');
  const [periodoHasta, setPeriodoHasta] = useState('');
  const [preview, setPreview] = useState(null);
  const [aiStatus, setAiStatus] = useState(null);
  const [useAi, setUseAi] = useState(false);
  const [downloadNotice, setDownloadNotice] = useState(null);
  // Envío del reporte diario por correo (Resend)
  const [sendLoading, setSendLoading] = useState(false);
  const [sendNotice, setSendNotice] = useState(null);
  const [sendError, setSendError] = useState(null);
  // Validación de datos DW por rango
  const [valDesde, setValDesde] = useState(firstOfMonthIso());
  const [valHasta, setValHasta] = useState(yesterdayIso());
  const [valLoading, setValLoading] = useState(false);
  const [valData, setValData] = useState(null);
  const [valError, setValError] = useState(null);

  const loadPeriods = useCallback(async () => {
    try {
      const res = await budgetService.getFordReportPeriods();
      if (res.success) setPeriods(res.data || []);
    } catch {
      /* silenciar */
    }
  }, []);

  const loadAiStatus = useCallback(async () => {
    try {
      const res = await budgetService.getFordReportAiStatus(false);
      if (res.success) setAiStatus(res.data);
    } catch {
      /* silenciar */
    }
  }, []);

  useEffect(() => {
    loadPeriods();
    loadAiStatus();
  }, [loadPeriods, loadAiStatus]);

  const periodoConsultaDesde = periodoDesde || periodoHasta || '';
  const periodoConsultaHasta = periodoHasta || periodoDesde || '';
  const isRange = periodoDesde && periodoHasta && periodoDesde !== periodoHasta;

  const handleGenerar = async () => {
    if (!periodoConsultaDesde) return;
    setLoading(true);
    setError(null);
    setPreview(null);
    try {
      const res = await budgetService.getFordReportPreview(
        periodoConsultaDesde,
        periodoConsultaHasta,
      );
      if (res.success) setPreview(res.data);
      else setError(res.error || 'Error cargando vista previa');
    } catch (err) {
      setError(err.response?.data?.message || err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDailyDownload = async () => {
    setDailyGenerating(true);
    setError(null);
    setDailyNotice(null);
    try {
      const { blob, alertCount, aiUsed, aiWarning, aiModel } =
        await budgetService.downloadFordDailyReport(dailyDate, true);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `FORD_REPORTE_DIARIO_${dailyDate.replace(/-/g, '')}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      const parts = [];
      if (aiUsed) {
        parts.push(`Narrativa Claude${aiModel ? ` (${aiModel})` : ''} aplicada en diagnósticos/alertas`);
      } else if (aiWarning) {
        parts.push(`Narrativa AI no aplicada — ${aiWarning}. Se usaron reglas determinísticas.`);
      } else {
        parts.push('Narrativa por reglas (Claude no disponible)');
      }
      if (alertCount !== null && Number(alertCount) > 0) {
        parts.push(`${alertCount} alerta(s) — hoja "4. ALERTAS TEMPRANAS"`);
      }
      setDailyNotice(parts.join(' · '));
    } catch (err) {
      setError(err.message || 'Error generando reporte diario');
    } finally {
      setDailyGenerating(false);
    }
  };

  const handleSendEmail = async () => {
    setSendLoading(true);
    setSendError(null);
    setSendNotice(null);
    try {
      const res = await budgetService.sendFordDailyReport(dailyDate);
      if (!res.success) {
        setSendError(res.error || 'No se pudo enviar el reporte');
        return;
      }
      const d = res.data || {};
      if (d.email?.sent) {
        setSendNotice(
          `Reporte enviado a: ${(d.email.recipients || []).join(', ')}` +
            (d.qaHallazgos ? ` · ${d.qaHallazgos} hallazgo(s) QA en el archivo` : ''),
        );
      } else {
        setSendError(
          `No se envió: ${d.email?.warning || 'Resend no configurado o sin destinatarios'}`,
        );
      }
    } catch (err) {
      setSendError(err.message || 'Error al enviar el reporte por correo');
    } finally {
      setSendLoading(false);
    }
  };

  const handleValidate = async () => {
    if (!valDesde || !valHasta) return;
    setValLoading(true);
    setValError(null);
    setValData(null);
    try {
      const res = await budgetService.getFordDailyValidation(valDesde, valHasta);
      if (res.success) setValData(res.data);
      else setValError(res.error || 'Error validando datos');
    } catch (err) {
      setValError(err.message || 'Error validando datos del DW');
    } finally {
      setValLoading(false);
    }
  };

  const handleDownload = async (type) => {
    if (!periodoConsultaDesde) return;
    setGenerating(type);
    setError(null);
    setDownloadNotice(null);
    const reportMeta = REPORT_TYPES.find((r) => r.id === type);
    const fileSlug = preview?.periodSlug || periodoConsultaHasta;
    try {
      const { blob, aiUsed, aiWarning } = await budgetService.downloadFordReport(
        periodoConsultaDesde,
        periodoConsultaHasta,
        type,
        useAi,
      );
      if (useAi && aiWarning) {
        setDownloadNotice(aiWarning);
      } else if (useAi && !aiUsed) {
        setDownloadNotice('Narrativa AI no se aplicó. El archivo incluye datos digitales + CRM.');
      }
      const ext = type === 'all' ? 'zip' : 'xlsx';
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `FORD_${fileSlug}_${type}.${ext}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError(err.message || `Error generando ${reportMeta?.label || type}`);
    } finally {
      setGenerating(null);
    }
  };

  return (
    <div className="p-3" style={{ background: '#f8f9fa', minHeight: '100vh' }}>
      <h4 className="mb-1 fw-bold" style={{ color: FORD_BLUE }}>
        <i className="fas fa-file-excel me-2" />
        Generador de Reportes Ford
      </h4>
      <small className="text-muted d-block mb-3">
        Selecciona el rango de períodos y genera los reportes desde Galaxy Metrics.
      </small>

      <Card className="mb-3 border-0 shadow-sm" style={{ borderLeft: `4px solid ${FORD_BLUE}` }}>
        <Card.Body className="py-3">
          <Row className="align-items-end g-3">
            <Col xs={12} md={5}>
              <div className="d-flex align-items-center mb-1">
                <i className="fas fa-calendar-day me-2" style={{ color: FORD_BLUE, fontSize: 18 }} />
                <span className="fw-bold" style={{ color: FORD_BLUE }}>Reporte Diario FQM</span>
                <Badge bg="success" className="ms-2 fw-normal">6 hojas</Badge>
                <Badge bg="info" className="ms-2 fw-normal">+ Claude</Badge>
              </div>
              <small className="text-muted">
                Números desde DW · narrativa + hoja "REVISIÓN QA" con Claude · envío por correo a managers Ford. Sin CRM.
              </small>
            </Col>
            <Col xs={12} md={3}>
              <Form.Label className="small fw-semibold mb-1">Datos hasta (D-1)</Form.Label>
              <Form.Control
                size="sm"
                type="date"
                value={dailyDate}
                max={yesterdayIso()}
                onChange={(e) => setDailyDate(e.target.value)}
              />
            </Col>
            <Col xs={12} md={4}>
              <Button
                variant="primary"
                className="w-100"
                disabled={!dailyDate || dailyGenerating}
                onClick={handleDailyDownload}
                style={{ background: FORD_BLUE, borderColor: FORD_BLUE }}
              >
                {dailyGenerating ? (
                  <><Spinner animation="border" size="sm" className="me-1" />Generando reporte...</>
                ) : (
                  <><i className="fas fa-file-excel me-1" />Descargar Reporte Diario</>
                )}
              </Button>
              <Button
                variant="outline-primary"
                size="sm"
                className="w-100 mt-2"
                disabled={!dailyDate || sendLoading}
                onClick={handleSendEmail}
                title="Genera y envía el reporte por correo a los managers Ford configurados"
              >
                {sendLoading ? (
                  <><Spinner animation="border" size="sm" className="me-1" />Enviando por correo...</>
                ) : (
                  <><i className="fas fa-paper-plane me-1" />Enviar por correo ahora</>
                )}
              </Button>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {dailyNotice && (
        <Alert variant="info" dismissible onClose={() => setDailyNotice(null)}>{dailyNotice}</Alert>
      )}

      {sendNotice && (
        <Alert variant="success" dismissible onClose={() => setSendNotice(null)}>
          <i className="fas fa-paper-plane me-2" />{sendNotice}
        </Alert>
      )}
      {sendError && (
        <Alert variant="warning" dismissible onClose={() => setSendError(null)}>
          <i className="fas fa-exclamation-triangle me-2" />{sendError}
        </Alert>
      )}

      <Card className="mb-3 border-0 shadow-sm" style={{ borderLeft: '4px solid #6c757d' }}>
        <Card.Body className="py-3">
          <div className="d-flex align-items-center mb-2">
            <i className="fas fa-database me-2" style={{ color: '#6c757d', fontSize: 18 }} />
            <span className="fw-bold" style={{ color: '#495057' }}>Validación de datos DW (por rango)</span>
            <Badge bg="secondary" className="ms-2 fw-normal">día × modelo</Badge>
          </div>
          <small className="text-muted d-block mb-2">
            Inspecciona los datos crudos del DW (inversión, leads, clics, alcance) por día y modelo, con el mismo cálculo (dedup NULL) que usa el reporte. Sirve para auditar los números.
          </small>
          <Row className="align-items-end g-2">
            <Col xs={6} md={3}>
              <Form.Label className="small fw-semibold mb-1">Desde</Form.Label>
              <Form.Control size="sm" type="date" value={valDesde} max={valHasta} onChange={(e) => setValDesde(e.target.value)} />
            </Col>
            <Col xs={6} md={3}>
              <Form.Label className="small fw-semibold mb-1">Hasta</Form.Label>
              <Form.Control size="sm" type="date" value={valHasta} max={yesterdayIso()} onChange={(e) => setValHasta(e.target.value)} />
            </Col>
            <Col xs={12} md={3}>
              <Button variant="secondary" size="sm" className="w-100" disabled={!valDesde || !valHasta || valLoading} onClick={handleValidate}>
                {valLoading ? (
                  <><Spinner animation="border" size="sm" className="me-1" />Consultando DW...</>
                ) : (
                  <><i className="fas fa-search me-1" />Validar datos</>
                )}
              </Button>
            </Col>
          </Row>

          {valError && <Alert variant="danger" className="mt-3 mb-0 py-2">{valError}</Alert>}

          {valData && (
            <div className="table-responsive mt-3" style={{ maxHeight: 420, overflow: 'auto' }}>
              <Table size="sm" bordered hover className="mb-0" style={{ fontSize: 12 }}>
                <thead style={{ position: 'sticky', top: 0, background: '#e9ecef' }}>
                  <tr>
                    <th>Fecha</th>
                    {valData.modelos.map((m) => <th key={m} className="text-end">{m}<br /><small className="text-muted">leads</small></th>)}
                    <th className="text-end">TOTAL<br /><small className="text-muted">leads / inv</small></th>
                  </tr>
                </thead>
                <tbody>
                  {valData.dias.map((d) => (
                    <tr key={d.fecha}>
                      <td className="fw-semibold">{d.fecha}</td>
                      {valData.modelos.map((m) => (
                        <td key={m} className="text-end" title={`inv ${fmtCurrency(d.porModelo[m]?.inversion)} · clics ${fmt(d.porModelo[m]?.clics)} · alcance ${fmt(d.porModelo[m]?.alcance)}`}>
                          {fmt(d.porModelo[m]?.leads)}
                        </td>
                      ))}
                      <td className="text-end fw-semibold">{fmt(d.total.leads)} / {fmtCurrency(d.total.inversion)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot style={{ position: 'sticky', bottom: 0, background: '#dee2e6' }}>
                  <tr className="fw-bold">
                    <td>TOTAL {valData.desde}→{valData.hasta}</td>
                    {valData.modelos.map((m) => <td key={m} className="text-end">{fmt(valData.totalPorModelo[m]?.leads)}</td>)}
                    <td className="text-end">{fmt(valData.granTotal.leads)} / {fmtCurrency(valData.granTotal.inversion)}</td>
                  </tr>
                </tfoot>
              </Table>
            </div>
          )}
        </Card.Body>
      </Card>

      <Card className="mb-3 border-0 shadow-sm">
        <Card.Body className="py-3">
          <Row className="align-items-end g-3">
            <Col xs={12} md={3}>
              <Form.Label className="small fw-semibold mb-1">Período Desde</Form.Label>
              <Form.Select
                size="sm"
                value={periodoDesde}
                onChange={(e) => {
                  setPeriodoDesde(e.target.value);
                  if (!periodoHasta || e.target.value > periodoHasta) {
                    setPeriodoHasta(e.target.value);
                  }
                  setPreview(null);
                }}
              >
                <option value="">Seleccionar</option>
                {periods.map((p) => (
                  <option key={p.periodo} value={p.periodo}>{p.periodo}</option>
                ))}
              </Form.Select>
            </Col>
            <Col xs={12} md={3}>
              <Form.Label className="small fw-semibold mb-1">Período Hasta</Form.Label>
              <Form.Select
                size="sm"
                value={periodoHasta}
                onChange={(e) => {
                  setPeriodoHasta(e.target.value);
                  setPreview(null);
                }}
              >
                <option value="">Seleccionar</option>
                {periods
                  .filter((p) => !periodoDesde || p.periodo >= periodoDesde)
                  .map((p) => (
                    <option key={p.periodo} value={p.periodo}>{p.periodo}</option>
                  ))}
              </Form.Select>
            </Col>
            <Col xs={12} md={3}>
              <div className="d-flex align-items-center gap-3">
                <Form.Check
                  type="switch"
                  id="use-ai-switch"
                  label={<span className="small">Narrativa AI</span>}
                  checked={useAi}
                  onChange={(e) => setUseAi(e.target.checked)}
                  disabled={!aiStatus?.configured}
                  title={aiStatus?.configured ? `Modelo: ${aiStatus.model}` : 'ANTHROPIC_API_KEY no configurada'}
                />
              </div>
            </Col>
            <Col xs={12} md={3}>
              <Button
                variant="primary"
                className="w-100"
                disabled={!periodoConsultaDesde || loading}
                onClick={handleGenerar}
                style={{ background: FORD_BLUE, borderColor: FORD_BLUE }}
              >
                {loading ? (
                  <><Spinner animation="border" size="sm" className="me-1" />Consultando...</>
                ) : (
                  <><i className="fas fa-search me-1" />Consultar Período</>
                )}
              </Button>
            </Col>
          </Row>
          {isRange && (
            <small className="text-muted d-block mt-2">
              <i className="fas fa-info-circle me-1" />
              Rango consolidado: <strong>{periodoDesde}</strong> a <strong>{periodoHasta}</strong>
              {' '}(métricas digitales + CRM sumadas en el período).
            </small>
          )}
        </Card.Body>
      </Card>

      {error && <Alert variant="danger" dismissible onClose={() => setError(null)}>{error}</Alert>}
      {downloadNotice && (
        <Alert variant="warning" dismissible onClose={() => setDownloadNotice(null)}>{downloadNotice}</Alert>
      )}

      {!preview && !loading && (
        <div className="text-center py-5">
          <i className="fas fa-chart-bar fa-3x mb-3" style={{ color: '#dee2e6' }} />
          <p className="text-muted">
            {periods.length > 0
              ? 'Selecciona un rango de períodos y pulsa "Consultar Período" para ver los datos disponibles.'
              : 'Cargando períodos disponibles...'}
          </p>
        </div>
      )}

      {preview && (
        <>
          <Row className="g-3 mb-3">
            <Col md={3}>
              <Card className="border-0 shadow-sm h-100" style={{ borderLeft: `4px solid ${FORD_BLUE}` }}>
                <Card.Body className="py-2">
                  <div className="text-muted small">Inversión Digital</div>
                  <div className="fs-4 fw-bold" style={{ color: FORD_BLUE }}>{fmtCurrency(preview.totals?.inversion)}</div>
                </Card.Body>
              </Card>
            </Col>
            <Col md={3}>
              <Card className="border-0 shadow-sm h-100" style={{ borderLeft: '4px solid #51cf66' }}>
                <Card.Body className="py-2">
                  <div className="text-muted small">Leads Plataforma</div>
                  <div className="fs-4 fw-bold text-success">{fmt(preview.totals?.leadsPlat)}</div>
                </Card.Body>
              </Card>
            </Col>
            {preview.crm?.available && (
              <Col md={3}>
                <Card className="border-0 shadow-sm h-100" style={{ borderLeft: '4px solid #845ef7' }}>
                  <Card.Body className="py-2">
                    <div className="text-muted small">Leads SGC (CRM)</div>
                    <div className="fs-4 fw-bold" style={{ color: '#845ef7' }}>{fmt(preview.crm.totalLeadsSGC)}</div>
                  </Card.Body>
                </Card>
              </Col>
            )}
            {preview.crm?.available && (
              <Col md={3}>
                <Card className="border-0 shadow-sm h-100" style={{ borderLeft: '4px solid #20c997' }}>
                  <Card.Body className="py-2">
                    <div className="text-muted small">Ventas (Facturado)</div>
                    <div className="fs-4 fw-bold" style={{ color: '#20c997' }}>{fmt(preview.crm.totalVentas)}</div>
                  </Card.Body>
                </Card>
              </Col>
            )}
            {!preview.crm?.available && (
              <>
                <Col md={3}>
                  <Card className="border-0 shadow-sm h-100" style={{ borderLeft: '4px solid #4dabf7' }}>
                    <Card.Body className="py-2">
                      <div className="text-muted small">Avisos / Anuncios</div>
                      <div className="fs-4 fw-bold" style={{ color: '#4dabf7' }}>{fmt(preview.totalAds)}</div>
                    </Card.Body>
                  </Card>
                </Col>
                <Col md={3}>
                  <Card className="border-0 shadow-sm h-100" style={{ borderLeft: '4px solid #fcc419' }}>
                    <Card.Body className="py-2">
                      <div className="text-muted small">Modelos activos</div>
                      <div className="fs-4 fw-bold" style={{ color: '#e67700' }}>{fmt(preview.modelos?.length)}</div>
                    </Card.Body>
                  </Card>
                </Col>
              </>
            )}
          </Row>

          {preview.crm?.available && (
            <Row className="g-3 mb-3">
              <Col md={2}>
                <Card className="border-0 shadow-sm h-100">
                  <Card.Body className="py-2 text-center">
                    <div className="text-muted" style={{ fontSize: 10 }}>Gestionados</div>
                    <div className="fw-bold">{fmt(preview.crm.totalGestionados)}</div>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={2}>
                <Card className="border-0 shadow-sm h-100">
                  <Card.Body className="py-2 text-center">
                    <div className="text-muted" style={{ fontSize: 10 }}>Citas</div>
                    <div className="fw-bold">{fmt(preview.crm.totalCitas)}</div>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={2}>
                <Card className="border-0 shadow-sm h-100">
                  <Card.Body className="py-2 text-center">
                    <div className="text-muted" style={{ fontSize: 10 }}>Show-up</div>
                    <div className="fw-bold">{fmt(preview.crm.totalShowup)}</div>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={2}>
                <Card className="border-0 shadow-sm h-100">
                  <Card.Body className="py-2 text-center">
                    <div className="text-muted" style={{ fontSize: 10 }}>Avisos</div>
                    <div className="fw-bold">{fmt(preview.totalAds)}</div>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={2}>
                <Card className="border-0 shadow-sm h-100">
                  <Card.Body className="py-2 text-center">
                    <div className="text-muted" style={{ fontSize: 10 }}>Modelos</div>
                    <div className="fw-bold">{fmt(preview.modelos?.length)}</div>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={2}>
                <Card className="border-0 shadow-sm h-100">
                  <Card.Body className="py-2 text-center">
                    <div className="text-muted" style={{ fontSize: 10 }}>Ventas USD</div>
                    <div className="fw-bold" style={{ fontSize: 13 }}>{fmtCurrency(preview.crm.totalVentasUSD)}</div>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          )}

          <Card className="mb-3 border-0 shadow-sm">
            <Card.Header className="py-2 text-white fw-bold" style={{ background: `linear-gradient(135deg, ${FORD_BLUE} 0%, #1a4a8a 100%)` }}>
              Resumen por Modelo — {preview.periodLabel}
              {preview.crm?.available && <Badge bg="light" text="dark" className="ms-2 fw-normal">+ CRM</Badge>}
            </Card.Header>
            <Card.Body className="p-0">
              <Table responsive hover size="sm" className="mb-0" style={{ fontSize: 13 }}>
                <thead style={{ background: '#f1f3f5' }}>
                  <tr>
                    <th className="ps-3">Modelo</th>
                    <th className="text-end">Inversión</th>
                    <th className="text-end">Leads Plat.</th>
                    {preview.crm?.available && <th className="text-end">Leads CRM</th>}
                    {preview.crm?.available && <th className="text-end">Gest.</th>}
                    {preview.crm?.available && <th className="text-end">Citas</th>}
                    {preview.crm?.available && <th className="text-end">Ventas</th>}
                    <th className="text-center">Canales</th>
                    <th className="text-end">Avisos</th>
                  </tr>
                </thead>
                <tbody>
                  {(preview.modelos || []).map((m) => (
                    <tr key={m.modelo}>
                      <td className="ps-3 fw-semibold">{m.modelo}</td>
                      <td className="text-end">{fmtCurrency(m.inversion)}</td>
                      <td className="text-end">{fmt(m.leads)}</td>
                      {preview.crm?.available && <td className="text-end">{fmtCrmCell(m.leadsAgencia, m.crmAplica)}</td>}
                      {preview.crm?.available && <td className="text-end">{fmtCrmCell(m.gestionados, m.crmAplica)}</td>}
                      {preview.crm?.available && <td className="text-end">{fmtCrmCell(m.citas, m.crmAplica)}</td>}
                      {preview.crm?.available && <td className="text-end">{fmtCrmCell(m.ventas, m.crmAplica)}</td>}
                      <td className="text-center"><Badge bg="light" text="dark" className="border">{m.canales}</Badge></td>
                      <td className="text-end">{fmt(m.ads)}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </Card.Body>
            {preview.crm?.available && (
              <Card.Footer className="py-2 text-muted small">
                <strong>N/A</strong> en Leads CRM / Gest. / Citas / Ventas = categoría de campaña digital (AON, MARCA, POSVENTA, EXONERADOS) sin familia de vehículo en el CRM SGC.
              </Card.Footer>
            )}
          </Card>

          <Card className="border-0 shadow-sm">
            <Card.Header className="py-2 fw-bold" style={{ background: '#f1f3f5' }}>
              <i className="fas fa-download me-2" />
              Descargar Archivos — {preview.periodLabel}
              {useAi && aiStatus?.configured && (
                <Badge bg="info" className="ms-2 fw-normal">+ Narrativa AI</Badge>
              )}
            </Card.Header>
            <Card.Body>
              <Row className="g-2">
                {REPORT_TYPES.map((r) => (
                  <Col key={r.id} xs={12} md={6} lg={4}>
                    <Card className="h-100 border">
                      <Card.Body className="d-flex flex-column">
                        <div className="d-flex align-items-center mb-2">
                          <i className={`fas fa-${r.icon} me-2`} style={{ color: FORD_BLUE, fontSize: 20 }} />
                          <span className="fw-semibold">{r.label}</span>
                        </div>
                        <small className="text-muted mb-3 flex-grow-1">{r.desc}</small>
                        <Button
                          variant={r.id === 'all' ? 'primary' : 'outline-primary'}
                          size="sm"
                          disabled={!!generating}
                          onClick={() => handleDownload(r.id)}
                          style={r.id === 'all' ? { background: FORD_BLUE, borderColor: FORD_BLUE } : {}}
                        >
                          {generating === r.id ? (
                            <><Spinner animation="border" size="sm" className="me-1" />Generando...</>
                          ) : (
                            <><i className="fas fa-download me-1" />Descargar</>
                          )}
                        </Button>
                      </Card.Body>
                    </Card>
                  </Col>
                ))}
              </Row>
            </Card.Body>
          </Card>
        </>
      )}
    </div>
  );
};

export default FordReportesPage;
